/**
 * The left rail: every run the API returned, newest first.
 *
 * Two levels rather than one because the chrome and the rows change for different
 * reasons: this file is layout — width, the collapse, the scroller, the filter — and
 * `RunList` is a row, the day grouping and the delete gate.
 *
 * ── The collapse ────────────────────────────────────────────────────────────────
 *
 * 288px at `xl` and up, a 56px strip of status dots below it. The rail is navigation
 * and the table beside it is the work; on a 1280px laptop, a run rail that keeps a
 * fifth of the width is taking it from the axis spine, which is the column this
 * whole UI exists to show. Pure CSS, so the first paint is already right — see the
 * note in `RunList`.
 *
 * The filter input goes with it, which would otherwise leave a rail that is showing
 * three of eight runs with nothing on screen to say why. So whenever a filter is
 * active the strip carries a clear-filter button in its place: the state is never
 * invisible, at either width.
 */
import { useMemo, useState } from 'react'
import { FilterX, Search } from 'lucide-react'
import { RunList } from './RunList'
import { Button } from './ui/Button'
import type { RunSummary } from '../api/types'
import { runStatusLook } from '../lib/runStatus'

interface Props {
  runs: RunSummary[]
  /** False until a run-list fetch has answered. See `RunList`'s empty branch. */
  runsLoaded: boolean
  /** True when the reason it has not answered is that it failed. */
  runsFailed: boolean
  selectedRunId: string | null
  onSelect: (runId: string) => void
  onDelete: (runId: string) => void
  deleting: string | null
  deleteError: string | null
}

/**
 * Substring match over the five things a reader might remember about a run.
 *
 * The prompt is in there and is the reason this is not an id search: nobody recalls
 * `run-58328a72`, they recall asking for grass-selective targets. The status is
 * matched on both the raw value and the label this UI shows for it, so typing
 * "blocked" and typing "no targets ranked" both work — the two are not the same
 * string and only one of them is on screen.
 */
function matches(run: RunSummary, needle: string): boolean {
  const haystack = [
    run.run_id,
    run.status,
    runStatusLook(run.status).label,
    run.stage,
    run.prompt,
  ]
  return haystack.some((field) => field?.toLowerCase().includes(needle))
}

export function RunRail({
  runs,
  runsLoaded,
  runsFailed,
  selectedRunId,
  onSelect,
  onDelete,
  deleting,
  deleteError,
}: Props) {
  const [query, setQuery] = useState('')
  const needle = query.trim().toLowerCase()
  const shown = useMemo(
    () => (needle ? runs.filter((run) => matches(run, needle)) : runs),
    [runs, needle],
  )

  return (
    <aside
      aria-label="Run history"
      className="flex w-14 shrink-0 flex-col border-r border-slate-800 xl:w-72"
    >
      {/* Outside the scroller, so fifty rows cannot push the filter off the top. */}
      <div className="shrink-0 px-2 pb-2 pt-3 xl:px-3">
        <div className="hidden xl:block">
          <label className="relative block">
            <span className="sr-only">Filter runs</span>
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter runs"
              className="h-8 w-full rounded-lg border border-slate-800 bg-slate-900 pl-8 pr-2 text-xs text-slate-100 placeholder:text-slate-500 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500"
            />
          </label>
          {needle && (
            <p className="mt-1.5 flex items-center justify-between gap-2 px-0.5 text-2xs text-slate-500">
              <span>
                {shown.length} of {runs.length} runs
              </span>
              <button
                type="button"
                onClick={() => setQuery('')}
                className="rounded text-slate-400 underline decoration-dotted hover:text-slate-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500"
              >
                Clear
              </button>
            </p>
          )}
        </div>

        {/* The strip's stand-in for the input above. Rendered only while a filter is
            actually hiding rows, because an always-present icon in a 56px column
            would be a control the width cannot support. */}
        {needle && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuery('')}
            aria-label={`Clear the run filter — showing ${shown.length} of ${runs.length} runs`}
            title={`Clear the run filter — showing ${shown.length} of ${runs.length} runs`}
            className="w-full px-0 text-amber-400 xl:hidden"
          >
            <FilterX className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-3">
        <RunList
          runs={shown}
          totalRuns={runs.length}
          runsLoaded={runsLoaded}
          runsFailed={runsFailed}
          selectedRunId={selectedRunId}
          onSelect={onSelect}
          onDelete={onDelete}
          deleting={deleting}
          deleteError={deleteError}
        />
      </div>
    </aside>
  )
}
